import AreaRegion from "@mapmycustomers/shared/types/territory/AreaRegion";
import AreaType from "@mapmycustomers/shared/types/territory/AreaType";
import Tile from "@mapmycustomers/shared/types/territory/Tile";
import { isDefined } from "@mapmycustomers/shared/util/assert";

export type ChangeListener = (areaType: AreaType) => void;

export class BoundaryCache {
  cache: Record<
    AreaType,
    {
      recordsCache: Record<number, Record<AreaRegion["id"], AreaRegion>>;
      tileCache: Record<Tile, AreaRegion["id"][]>;
      zoomCache: Record<number, AreaRegion[]>;
    }
  > = {};

  changeListeners: Record<AreaType, ChangeListener[]> = {};

  addChangeListener(areaType: AreaType, listener: ChangeListener) {
    if (!this.changeListeners[areaType]) {
      this.changeListeners[areaType] = [];
    }
    this.changeListeners[areaType].push(listener);
    return {
      remove: () => {
        this.changeListeners[areaType] = this.changeListeners[areaType].filter(
          (item) => item !== listener
        );
      },
    };
  }

  addRegionsForZoomLevel(areaType: AreaType, zoomLevel: number, regions: AreaRegion[]) {
    this._ensureAreaTypeCacheExists(areaType);
    this.cache[areaType].zoomCache[zoomLevel] = regions;
    this._notifyListeners(areaType);
  }

  addTilesInformation(areaType: AreaType, tile: Tile, regionIds: AreaRegion["id"][]) {
    this._ensureAreaTypeCacheExists(areaType);
    this.cache[areaType].tileCache[tile] = regionIds;
    this._notifyListeners(areaType);
  }

  addRegionForZoomLevel(areaType: AreaType, zoomLevel: number, region: AreaRegion) {
    this._ensureAreaTypeCacheExists(areaType);
    if (!this.cache[areaType].recordsCache[zoomLevel]) {
      this.cache[areaType].recordsCache[zoomLevel] = {};
    }
    this.cache[areaType].recordsCache[zoomLevel][region.id] = region;
    this._notifyListeners(areaType);
  }

  hasRegionsForZoomLevel(areaType: AreaType, zoomLevel: number) {
    return !!this.cache[areaType]?.zoomCache[zoomLevel];
  }

  hasTileInformation(areaType: AreaType, tile: Tile) {
    return !!this.cache[areaType]?.tileCache[tile];
  }

  hasRegionForZoomLevel(areaType: AreaType, zoomLevel: number, regionId: AreaRegion["id"]) {
    return !!this.cache[areaType]?.recordsCache[zoomLevel]?.[regionId];
  }

  getTileInformation(areaType: AreaType, tile: Tile): AreaRegion["id"][] {
    return this.cache[areaType]?.tileCache[tile] ?? [];
  }

  getRegions(areaType: AreaType, zoomLevel: number, tiles?: Tile[]): AreaRegion[] {
    if (!this.cache[areaType]) {
      return [];
    }
    if (!tiles?.length) {
      return this.cache[areaType].zoomCache[zoomLevel] ?? [];
    }
    const regionIds = Array.from(
      new Set(tiles.flatMap((tile) => this.cache[areaType].tileCache[tile] ?? []))
    );
    return regionIds
      .map((regionId) => this.cache[areaType].recordsCache[zoomLevel]?.[regionId])
      .filter(isDefined);
  }

  private _notifyListeners(areaType: AreaType) {
    (this.changeListeners[areaType] ?? []).forEach((listener) => {
      listener(areaType);
    });
  }

  private _ensureAreaTypeCacheExists(areaType: AreaType) {
    if (!this.cache[areaType]) {
      this.cache[areaType] = {
        recordsCache: {},
        tileCache: {},
        zoomCache: {},
      };
    }
  }
}

const boundaryCache = new BoundaryCache();

export default boundaryCache;
